import { BehaviorSubject, lastValueFrom, Observable } from "rxjs";
import { map } from "rxjs/operators";

import { Injectable } from "@angular/core";

import { platform } from "../../platform/platform";
import { LOCAL_STORAGE, MODE, PLATFORM } from "../app.constants";
import { Capacitor } from "@capacitor/core";
import { mode } from "../../mode/mode";
import { AppInfoDbService } from "./database/app-info-db.service";

export type ApplicationMode = { platform: "web" } | { platform: "desktop"; mode: "offline" | "online" | "onPremise" };

export const DesktopOnline: ApplicationMode = { platform: "desktop", mode: "online" };

const DesktopOffline: ApplicationMode = { platform: "desktop", mode: "offline" };

const DesktopOnPremise: ApplicationMode = { platform: "desktop", mode: "onPremise" };

const Web: ApplicationMode = { platform: "web" };

enum OperatingSystem {
  Android,
  iOS,
  Linux,
  MacOS,
  Windows,
  Unknown
}

@Injectable({
  providedIn: "root"
})
export class NetworkStatusService {
  constructor(private appInfoDbService: AppInfoDbService) {}

  initPlatform(): ApplicationMode {
    let savedPlatform = localStorage.getItem(LOCAL_STORAGE.VISOL_PLATFORM);

    if (savedPlatform === null) {
      const currentPlatform = platform.name === PLATFORM.DESKTOP ? DesktopOnPremise : Web;
      localStorage.setItem(LOCAL_STORAGE.VISOL_PLATFORM, JSON.stringify(currentPlatform));
    }

    savedPlatform = localStorage.getItem(LOCAL_STORAGE.VISOL_PLATFORM);
    const currentPlatform = JSON.parse(savedPlatform);

    return currentPlatform;
  }

  private applicationModeSubject$ = new BehaviorSubject<ApplicationMode>(this.initPlatform());

  public applicationMode$ = this.applicationModeSubject$.asObservable();

  public get isOnline(): boolean {
    const value = this.applicationModeSubject$.getValue();
    const isOnline = value.platform === "web" || (value.platform === "desktop" && value.mode === "online");
    return isOnline;
  }

  public get isOffline(): boolean {
    return this.isDesktop() && !this.isOnline;
  }

  public get isBrianMode(): boolean {
    return mode.name === MODE.BRIAN;
  }

  public isDesktop(): boolean {
    return this.applicationModeSubject$.getValue().platform === PLATFORM.DESKTOP;
  }

  public get isDesktopAndOffline() {
    return this.isDesktop() ? !this.isOnline : false;
  }

  public get isOnPremise$(): Observable<boolean> {
    return this.applicationModeSubject$.pipe(map((application) => application.platform === PLATFORM.DESKTOP && application.mode === "onPremise"));
  }

  public get isOnPremise(): boolean {
    const applicationMode = this.applicationModeSubject$.getValue();
    return applicationMode?.platform === PLATFORM.DESKTOP && applicationMode.mode === "onPremise";
  }

  private getOperatingSystem(): OperatingSystem {
    const userAgent = window.navigator.userAgent.toLowerCase();

    if (/android/i.test(userAgent)) return OperatingSystem.Android;
    if (/\b(iphone|ipad|ipod)\b/i.test(userAgent)) return OperatingSystem.iOS;
    if (/\blinux\b/i.test(userAgent)) return OperatingSystem.Linux;
    if (/\bmac\b/i.test(userAgent)) return OperatingSystem.MacOS;
    if (/\bwin(dows)?\b/i.test(userAgent)) return OperatingSystem.Windows;

    return OperatingSystem.Unknown;
  }

  public useApplicationFileManager(): boolean {
    return this.isBrianMode && this.isDesktop() && this.getOperatingSystem() === OperatingSystem.Windows;
  }

  public static isVisolOffline(mode: ApplicationMode): boolean {
    return mode.platform === "desktop" && mode.mode === "offline";
  }

  public isOnlineObserver(): Observable<boolean> {
    return this.applicationModeSubject$.pipe(
      map((application) => (application.platform === PLATFORM.DESKTOP && application.mode === "online") || application.platform === "web")
    );
  }

  public async switchOnlineMode() {
    if (this.isDesktop()) {
      localStorage.setItem(LOCAL_STORAGE.VISOL_PLATFORM, JSON.stringify(DesktopOnline));
      await lastValueFrom(this.appInfoDbService.putNetworkMode(true));
      this.applicationModeSubject$.next(DesktopOnline);
    }
    return this.isOnline;
  }

  public async switchOfflineMode() {
    if (this.isDesktop()) {
      await lastValueFrom(this.appInfoDbService.putNetworkMode(false));
      localStorage.setItem(LOCAL_STORAGE.VISOL_PLATFORM, JSON.stringify(DesktopOffline));
      this.applicationModeSubject$.next(DesktopOffline);
    }
    return this.isOnline;
  }

  public setIsOnPremise(isCurrentOfflineUser: boolean): void {
    if (this.isDesktop() && isCurrentOfflineUser) {
      this.applicationModeSubject$.next(DesktopOnPremise);
      localStorage.setItem(LOCAL_STORAGE.VISOL_PLATFORM, JSON.stringify(DesktopOnPremise));
    }
  }

  public isMobileDevice() {
    return Capacitor.isNativePlatform() || Capacitor.getPlatform() === "ios" || Capacitor.getPlatform() === "android";
  }

  public isIOSDevice() {
    return Capacitor.getPlatform() === "ios";
  }
}
